import { getEventDisplayImageRef, getEventMobileDisplayImageRef, getEventNextOccurrence, normalizeImageRefs } from './imageUtils';
import { isHiddenTicketRef } from './ticketLinks';
import { getTodayKey } from './dateKey';
import { isCancelledStatus } from './eventStatus';

export const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&h=600&fit=crop';

// Single source of truth for the display title: preferred_title (cleaned/admin-curated)
// wins over the raw provider title. Use this everywhere a user-facing title is shown.
export const getEventDisplayTitle = (event) =>
  event?.preferred_title || event?.title || event?.name || '';

// Same contract for performers: preferred_name (admin-curated) wins over the provider's name.
// Artist refs on an event carry only `name`; `preferred_name` arrives from the artist document
// on responses that enrich them, so this falls back cleanly where it doesn't.
export const getArtistDisplayName = (artist) =>
  (typeof artist === 'string' ? artist : artist?.preferred_name || artist?.name) || '';

/**
 * The artist refs on a mapped event — the objects, whichever field the mapper left them in.
 *
 * The two mappers that feed the event surfaces do not agree about `artists`, and reading
 * it directly is how a feature silently stops working on two thirds of the app:
 *
 * - `mapApiEvent` (related events) puts the **refs** in `artists`.
 * - `mapFeedEvent` (every event list: feed, map, search, likes, dislikes) puts
 *   **display names** in `artists`, and keeps the refs under `_artists`.
 *
 * `EventCard`'s favourite-artist star read `artists` and so could never match on the feed
 * or in search: `isFavoriteArtist` keys on `id`/`mbid`, and a name string has neither.
 *
 * Strings are dropped rather than passed along, because every caller here wants a ref it can
 * *identify* — a favourite test, a genre read. Anything that wants a label uses
 * `getArtistDisplayName`, which takes the legacy plain-string ref on purpose.
 *
 * @param {Object} event - A mapped event
 * @returns {Array<Object>} The identifiable artist refs, possibly empty
 */
export const getEventArtistRefs = (event) => {
  const refs = Array.isArray(event?._artists) ? event._artists : event?.artists;
  return Array.isArray(refs) ? refs.filter((artist) => artist && typeof artist === 'object') : [];
};

// An event's genre names: its line-up's, or the event's own curated ones when the line-up has
// none (#754) — the API's `event_genre_labels` rule. Either list may hold refs or plain names.
export const eventGenreNames = (lineupGenres, curatedGenres) => {
  const names = (list) => (Array.isArray(list) ? list : [])
    .map(genre => (typeof genre === 'string' ? genre : genre?.name))
    .filter(Boolean);
  const lineup = names(lineupGenres);
  return lineup.length > 0 ? lineup : names(curatedGenres);
};

// Same contract for the description: `ai_description` is written by an agent and reviewed by an
// admin before it lands, and it exists precisely because the ingested `description` is usually
// marketing copy, booking boilerplate and HTML leftovers. So it wins outright when set. The
// provider's text is kept on the event and is what shows when it is not — which is also what
// makes clearing the AI one a real undo rather than a deletion.
export const getEventDescription = (event) =>
  event?.ai_description?.trim() || event?.description?.trim() || '';

// Same contract for the venue an event happens at. The venue document's admin-curated
// `preferred_name` wins over the provider's `name`; `event.venue_name` is the denormalized
// provider value carried on the event itself, and is the last resort before the placeholder —
// it is never rewritten when an admin curates a name, so it must not be preferred over the
// venue object. Takes the API event (not the venue) because that fallback lives on the event.
// '' when nothing names it: the placeholder is words, so it is the render site's, in the
// reader's language (`labels:misc.venueTba`).
export const getEventVenueName = (event) => {
  const venue = event?.venue;
  if (typeof venue === 'string') return venue || event?.venue_name || '';
  return venue?.preferred_name || venue?.name || event?.venue_name || '';
};

// A mapped event's venue as shown: its name, or the placeholder in the reader's language.
// `t` is the caller's translator (the key carries its `labels:` namespace).
export const venueLabel = (t, venue) => venue || t('labels:misc.venueTba');

// Single source of truth for "town (metro)" display: the venue's own city stays visible
// (matters for getting there) with the metro it's discoverable under in parentheses,
// unless they're the same name. Use this everywhere a city renders — don't reimplement
// the parenthetical per component.
export const formatCityDisplay = ({ city, mainPopulation } = {}) => {
  const trimmedCity = city?.trim() || null;
  const trimmedPopulation = mainPopulation?.trim() || null;

  if (!trimmedCity) return trimmedPopulation;
  if (!trimmedPopulation || trimmedPopulation.toLowerCase() === trimmedCity.toLowerCase()) {
    return trimmedCity;
  }
  return `${trimmedCity} (${trimmedPopulation})`;
};

// Parses an event date for local calendar-day comparisons/grouping. Bare
// "yyyy-MM-dd" strings (e.g. `local_date`) are parsed as local midnight —
// `new Date("yyyy-MM-dd")` parses them as UTC midnight per spec, which shifts
// the calendar day backward a day in negative-UTC-offset timezones. Full
// timestamps (with a time component) parse as-is. Returns null for empty input.
export const parseEventDate = (dateStr) => {
  if (!dateStr) return null;
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  const parsed = new Date(dateStr);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// The short date the feed prints under an event — "Sat, Jun 1", with the year added only
// when it is not this one. Lifted out of `EventCard`, where it was redeclared on every
// render and where a second copy had already drifted out of the year rule; a util is also
// the only place the year branch can be tested directly, which is what lets that drift
// fail loudly next time.
//
// `locale` is the app's language (`i18n.language`, read through the caller's
// `useTranslation` so a switch re-renders): "Sat, Jun 1" / "sáb, 1 jun".
//
// Returns null rather than the literal "Invalid Date" for an event with no resolvable date (#363).
export const formatEventDateLabel = (dateStr, locale) => {
  const date = parseEventDate(dateStr);
  if (!date) return null;
  const options = { weekday: 'short', month: 'short', day: 'numeric' };
  if (date.getFullYear() !== new Date().getFullYear()) {
    options.year = 'numeric';
  }
  return date.toLocaleDateString(locale, options);
};

export const normalizeUrlRefs = (urls) => {
  if (!Array.isArray(urls)) return [];

  return urls
    .map((urlRef) => {
      if (typeof urlRef === 'string') {
        return { url: urlRef, provider: null };
      }

      if (urlRef && typeof urlRef.url === 'string' && urlRef.url) {
        return {
          url: urlRef.url,
          provider: urlRef.provider || null,
          // Carry the admin suppression marker through so ticketLinks can hide it.
          ...(urlRef.bad === true ? { bad: true } : {}),
        };
      }

      return null;
    })
    .filter(Boolean);
};

// First ref we may surface — skips suppressed refs (admin-marked `bad`, resale/excluded
// providers). The single rule used everywhere a "primary"/"ticket" URL is picked, so a hidden
// link is never chosen as the one to show. Expects already-normalized refs.
const firstVisibleRef = (refs) => (refs || []).find((ref) => !isHiddenTicketRef(ref)) || null;

// The primary ticket URL (used by list/card "Get Tickets" buttons) must skip suppressed refs.
export const getPrimaryUrlRef = (urls) => firstVisibleRef(normalizeUrlRefs(urls));

export const getPrimaryUrl = (urls) => getPrimaryUrlRef(urls)?.url || null;

export { normalizeImageRefs } from './imageUtils';

// Merge srcUrls into target's urls array (deduped by URL string), updating ticketUrlRef/ticketUrl.
const mergeUrls = (target, srcUrls) => {
  const seen = new Set(target.urls.map(u => u.url));
  for (const u of srcUrls) {
    if (!seen.has(u.url)) { target.urls.push(u); seen.add(u.url); }
  }
  if (!target.ticketUrlRef && target.urls.length > 0) {
    target.ticketUrlRef = firstVisibleRef(target.urls);
    target.ticketUrl = target.ticketUrlRef?.url || null;
  }
};

// A show listed as several ticket tiers is live while any of its tiers is, so one withdrawn tier
// cannot cancel the night: a cancelled base gives way to the first live tier, which brings its own
// status, times and label. The links stay merged, and the id stays the base's, so the show keeps one
// key (`getOccurrenceKey`) whichever of its tiers is cancelled. Doors and set times need no merging of their own:
// the API puts what a show's tiers agree on onto every tier (`share_show_times`, #792).
const keepShowLive = (target, occ) => {
  if (!isCancelledStatus(target.status) || !occ.status || isCancelledStatus(occ.status)) return;
  const { id, urls, ticketUrlRef, ticketUrl } = target;
  Object.assign(target, occ, { id, urls, ticketUrlRef, ticketUrl });
};

/**
 * Normalize, sort, and aggregate raw occurrence objects from the API.
 *
 * - Collapses occurrences sharing the same date+time into one entry, merging their URLs.
 * - Distributes URLs from timeless occurrences (no local_time / no_specific_time) to every
 *   timed occurrence on the same date, then drops the timeless entry.
 * - If a date has only timeless occurrences, merges them into a single entry.
 *
 * Each URL ref gains an `occurrenceName` field (from its source occurrence's `name`) so
 * ticket buttons can be labelled (e.g. "VIP", "General Admission").
 */
export const aggregateOccurrences = (rawOccurrences) => {
  if (!Array.isArray(rawOccurrences) || rawOccurrences.length === 0) return [];

  const withNames = rawOccurrences.filter(Boolean).map(occ => {
    const occName = occ?.name || null;
    const urls = normalizeUrlRefs(occ?.urls).map(u => ({ ...u, occurrenceName: occName }));
    const ticketUrlRef = firstVisibleRef(urls);
    return { ...occ, urls, ticketUrlRef, ticketUrl: ticketUrlRef?.url || null };
  });

  withNames.sort((a, b) => {
    const da = `${a.local_date || ""}T${a.local_time || "00:00"}`;
    const db = `${b.local_date || ""}T${b.local_time || "00:00"}`;
    return da < db ? -1 : da > db ? 1 : 0;
  });

  // Group by date, then within each date separate timed vs timeless occurrences.
  const dateGroups = new Map();
  for (const occ of withNames) {
    const date = occ.local_date || '';
    if (!dateGroups.has(date)) dateGroups.set(date, { timedMap: new Map(), noTimeOccs: [] });
    const group = dateGroups.get(date);
    const isNoTime = !occ.local_time || occ.no_specific_time;
    if (isNoTime) {
      group.noTimeOccs.push(occ);
    } else {
      const timeKey = (occ.local_time || '').substring(0, 5);
      if (!group.timedMap.has(timeKey)) {
        group.timedMap.set(timeKey, { ...occ, urls: [...occ.urls] });
      } else {
        mergeUrls(group.timedMap.get(timeKey), occ.urls);
        keepShowLive(group.timedMap.get(timeKey), occ);
      }
    }
  }

  const result = [];
  for (const { timedMap, noTimeOccs } of dateGroups.values()) {
    const timedList = [...timedMap.values()];
    if (timedList.length > 0) {
      // Distribute URLs from timeless occurrences to every timed slot on this date.
      for (const timedOcc of timedList) {
        for (const noTimeOcc of noTimeOccs) {
          mergeUrls(timedOcc, noTimeOcc.urls);
        }
      }
      result.push(...timedList);
    } else {
      // No timed occurrences — merge all timeless ones into a single entry.
      if (noTimeOccs.length > 0) {
        const merged = { ...noTimeOccs[0], urls: [...noTimeOccs[0].urls] };
        for (let i = 1; i < noTimeOccs.length; i++) {
          mergeUrls(merged, noTimeOccs[i].urls);
          keepShowLive(merged, noTimeOccs[i]);
        }
        result.push(merged);
      }
    }
  }

  return result;
};

export const mapApiEvent = (event) => {
  const venueObj = event.venue;
  const venueName = getEventVenueName(event);

  const artistsData = event._artists || event.artists;
  const artistName = getArtistDisplayName(artistsData?.[0]) || event.artist || 'Artist TBA';
  const artistsArray = artistsData?.map(getArtistDisplayName) || [];

  // The same next showing the cards date the event by, from the viewer's day on — for a
  // long run too, whose `next_occurrence` the API picks against the venue's day (#626).
  const upcomingOccurrence = getEventNextOccurrence(event);

  const eventUrls = normalizeUrlRefs(event.urls);
  const eventImages = normalizeImageRefs(event.images);

  const mappedOccurrences = aggregateOccurrences(event.occurrences || []);

  const primaryEventUrlRef = firstVisibleRef(eventUrls);
  const singleOccurrenceTicketUrl = mappedOccurrences.length === 1
    ? mappedOccurrences[0]?.ticketUrl || null
    : null;
  const singleOccurrenceTicketUrlRef = mappedOccurrences.length === 1
    ? mappedOccurrences[0]?.ticketUrlRef || null
    : null;

  // occurrence_dates before event.date: a finished run is shipped as a lazy payload with
  // no next_occurrence and no occurrences array (the API summarizes it rather than sending
  // hundreds of past dates), and without this the card fell through to `new Date()` and
  // advertised a run that ended months ago as happening today.
  const eventDate =
    upcomingOccurrence?.local_date || event.occurrence_dates?.[0] || event.date || new Date().toISOString();
  const rawTime = upcomingOccurrence?.local_time;
  // '20:00' stands in for an occurrence in the array that carries no local_time. A long
  // run has no time to stand in for: a finished one ships no next_occurrence, and one
  // trimmed to the viewer's day knows only the date (the calendar's day fetch brings
  // that day's time and status to the ticket panel). Consumers already guard on falsy time.
  const eventTime = rawTime ? rawTime.substring(0, 5) : (event.occurrences?.length ? '20:00' : null);
  // A long run's dates from the viewer's day on, so the title's range, the calendar's
  // opening day and its day fetch agree with `eventDate`. A run the API calls finished
  // (no next_occurrence) keeps them all, even for a viewer still on its last night.
  const todayKey = getTodayKey();
  const viewerOccurrenceDates = event.next_occurrence
    ? event.occurrence_dates?.filter((date) => date >= todayKey)
    : null;

  let fullAddress = null;
  const venueAddress = venueObj?.address;
  if (venueAddress) {
    if (typeof venueAddress === 'string') {
      fullAddress = venueAddress;
    } else if (typeof venueAddress === 'object') {
      const addressParts = [
        venueAddress.line1 || venueAddress.street || venueAddress.streetAddress,
        venueAddress.line2,
        venueAddress.city,
        venueAddress.state || venueAddress.province,
        venueAddress.zipCode || venueAddress.postalCode || venueAddress.zip,
        venueAddress.country,
      ].filter(Boolean);
      fullAddress = addressParts.join(', ');
    }
  }

  const bestHeroImageRef = getEventDisplayImageRef(event, FALLBACK_IMAGE);
  const mobileHeroImageRef = getEventMobileDisplayImageRef(event, FALLBACK_IMAGE);
  const bestHeroImage = bestHeroImageRef.url;

  const venueCapacityBucket = venueObj?.capacity_bucket || event.venueSize || venueObj?.size || null;
  const venueTypeHint = venueObj?.venue_type_hint || venueObj?.type || null;
  const venueModality = Array.isArray(venueObj?.modality) ? venueObj.modality : [];

  return {
    id: event.id,
    name: getEventDisplayTitle(event),
    artist: artistName,
    domain: event.domain || null,
    heroImage: bestHeroImage,
    heroImageRef: bestHeroImageRef,
    mobileHeroImage: mobileHeroImageRef.url,
    mobileHeroImageRef,
    image: bestHeroImage,
    description: getEventDescription(event),
    urls: eventUrls,
    ticketUrlRef: primaryEventUrlRef || singleOccurrenceTicketUrlRef,
    ticketUrl: primaryEventUrlRef?.url || singleOccurrenceTicketUrl,
    date: eventDate,
    time: eventTime,
    venue: venueName,
    address: fullAddress,
    venueSize: venueCapacityBucket,
    venueType: venueTypeHint,
    venueSetting: venueModality[0] || venueObj?.setting || null,
    venueDisposition: Array.isArray(venueObj?.disposition) ? venueObj.disposition : [],
    venueModality,
    venueCapacityBucket,
    venueCapacityRange: venueObj?.capacity_range || null,
    // Numeric bounds behind that English label (#768); VenueSection formats them in the reader's language.
    venueCapacityBounds: venueObj?.capacity_range_bounds || null,
    venueTypeHint,
    venueInferredContext: venueObj?.venue_inferred_context || null,
    wheelchairAccessible: venueObj?.wheelchair_accessible || null,
    publicTransportAccess: venueObj?.public_transport_access || null,
    foodAndDrinks: venueObj?.food_and_drinks || null,
    venueUrl: venueObj?.url || null,
    venueLatitude: venueObj?.latitude || null,
    venueLongitude: venueObj?.longitude || null,
    venueGooglePlaceId: venueObj?.google_place_id || null,
    capacity: venueObj?.capacity || null,
    // No `ticketPrice` here: the API has no `price` field — only `start_price` — so
    // `event.price || 'TBA'` was permanently 'TBA'. Price rendering reads startPrice/isFree.
    startPrice: event.start_price || null,
    isFree: event.start_price ? Number(event.start_price.price) === 0 : null,
    city: venueObj?.city || (typeof venueAddress === 'object' ? venueAddress?.city : null) || null,
    // Venue's main_population wins when there is a venue; event.main_population is the
    // only source for venue-less events.
    mainPopulation: venueObj?.main_population || event.main_population || null,
    matchScore: event.matchScore ?? 0,
    distance: null,
    // Run time of one performance, in minutes (#792): the provider's or event research's.
    durationMinutes: event.duration_minutes ?? null,
    // The event's own genres, which `ForYouCard` and `getAllEventGenres` show when its
    // line-up has none (#754).
    curatedGenres: event.curated_genres || [],
    genres: [...new Set(eventGenreNames(
      (artistsData || []).flatMap(a => (typeof a === 'string' ? [] : (a.genres || []))),
      event.curated_genres
    ))],
    ageRestriction: null,
    venuePhone: venueObj?.phone || null,
    venueWebsite: venueObj?.website || primaryEventUrlRef?.url || null,
    amenities: [],
    occurrencesLazy: event.occurrences_lazy || false,
    occurrenceDates: viewerOccurrenceDates?.length ? viewerOccurrenceDates : (event.occurrence_dates || null),
    nextOccurrence: event.next_occurrence || null,
    occurrences: mappedOccurrences,
    artists: artistsData || [],
    performers: artistsArray.map(name => ({ name })),
    isLiked: event.user_interaction === 'like',
    isDisliked: event.user_interaction === 'dislike',
    recommendationExplanation: event.recommendation_explanation || null,
    experienceContext: event.experience_context || null,
    source: event.source || null,
    source_ids: event.source_ids || null,
    images: eventImages,
  };
};


export const getOccurrenceKey = (occurrence, fallbackIndex = 0) => {
  if (!occurrence) return `occurrence-${fallbackIndex}`;

  return occurrence.id
    || [occurrence.local_date, occurrence.local_time, occurrence.date_time, occurrence.status, fallbackIndex]
      .filter(Boolean)
      .join('|')
    || `occurrence-${fallbackIndex}`;
};
